import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Video, Upload, Trash2, RefreshCw, RotateCcw } from 'lucide-react';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const fileSig = (f) => `wr_upl_${f.name}_${f.size}_${f.lastModified}`;

const WorkshopRecordingsAdmin = ({ API_URL }) => {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [resumeHint, setResumeHint] = useState('');
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const token = () => localStorage.getItem('admin_session_token');

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/workshop-recordings?session_token=${token()}`);
      if (res.ok) { const d = await res.json(); setItems(d.recordings || []); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchList(); }, []);

  // Detect a saved (partial) upload for the chosen file to show a resume hint.
  const onFileChange = () => {
    const f = fileRef.current?.files?.[0];
    setResumeHint('');
    if (!f) return;
    const saved = localStorage.getItem(fileSig(f));
    if (saved) {
      try {
        const s = JSON.parse(saved);
        if (s.title && !title) setTitle(s.title);
        setResumeHint('A previous upload for this file was found — it will resume from where it stopped.');
      } catch (e) { /* ignore */ }
    }
  };

  const uploadChunkWithRetry = async (uploadId, index, blob) => {
    let lastErr = '';
    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_number', index);
        fd.append('file', blob, `chunk_${index}`);
        const res = await fetch(`${API_URL}/api/admin/workshop-recordings/upload-chunk?session_token=${token()}`, { method: 'POST', body: fd });
        if (res.ok) return;
        lastErr = `HTTP ${res.status}`;
      } catch (err) {
        lastErr = err.message || 'network error';
      }
      if (attempt < 5) await new Promise((r) => setTimeout(r, attempt * 1500));
    }
    throw new Error(`Chunk ${index + 1} failed (${lastErr})`);
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { alert('Please choose a video file'); return; }
    if (!title.trim()) { alert('Please enter a title'); return; }
    setUploading(true); setProgress(0); setStatusText('Preparing…');

    const sigKey = fileSig(file);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      let uploadId = null;
      let received = new Set();

      // Try to resume a saved session for this exact file.
      const saved = localStorage.getItem(sigKey);
      if (saved) {
        try {
          const s = JSON.parse(saved);
          if (s.upload_id && s.total_chunks === totalChunks) {
            const st = await fetch(`${API_URL}/api/admin/workshop-recordings/upload-status?upload_id=${s.upload_id}&session_token=${token()}`);
            if (st.ok) {
              const d = await st.json();
              uploadId = s.upload_id;
              received = new Set(d.received_chunks || []);
              setStatusText(`Resuming — ${received.size}/${totalChunks} chunks already uploaded`);
            }
          }
        } catch (e) { /* fall through to fresh init */ }
      }

      // No resumable session → start fresh.
      if (!uploadId) {
        const initRes = await fetch(`${API_URL}/api/admin/workshop-recordings/init-upload?session_token=${token()}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, content_type: file.type || 'video/mp4', total_chunks: totalChunks, chunk_size: CHUNK_SIZE, file_size: file.size }),
        });
        if (!initRes.ok) throw new Error('Failed to start upload');
        uploadId = (await initRes.json()).upload_id;
        received = new Set();
      }

      // Persist session so the upload can resume after a drop/reload.
      localStorage.setItem(sigKey, JSON.stringify({ upload_id: uploadId, total_chunks: totalChunks, title: title.trim() }));

      // Upload remaining chunks with a concurrency pool (parallel + retry + resume-safe).
      const pending = [];
      for (let i = 0; i < totalChunks; i++) if (!received.has(i)) pending.push(i);

      let done = received.size;
      setProgress(Math.round((done / totalChunks) * 100));
      setStatusText(`Uploading ${pending.length} of ${totalChunks} chunks…`);

      const CONCURRENCY = Math.min(4, pending.length || 1);
      let cursor = 0;
      let failed = null;
      const worker = async () => {
        while (cursor < pending.length && !failed) {
          const i = pending[cursor++];
          const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          try {
            await uploadChunkWithRetry(uploadId, i, blob);
            done += 1;
            setProgress(Math.round((done / totalChunks) * 100));
            setStatusText(`Uploaded ${done} of ${totalChunks} chunks…`);
          } catch (err) {
            failed = err;
          }
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
      if (failed) throw failed;

      setStatusText('Finalizing…');
      const compRes = await fetch(`${API_URL}/api/admin/workshop-recordings/complete-upload?session_token=${token()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: uploadId, title: title.trim() }),
      });
      if (!compRes.ok) {
        const err = await compRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to finalize upload');
      }

      localStorage.removeItem(sigKey);
      setTitle('');
      if (fileRef.current) fileRef.current.value = '';
      setResumeHint('');
      await fetchList();
      alert('Recording uploaded successfully');
    } catch (e) {
      alert(`${e.message || 'Upload failed'}\n\nYour progress is saved — re-select the same file and click Upload to resume from where it stopped.`);
    } finally {
      setUploading(false); setProgress(0); setStatusText('');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recording? Enrolled users will lose access to it.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/workshop-recordings/${id}?session_token=${token()}`, { method: 'DELETE' });
      if (res.ok) fetchList();
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="workshop-recordings-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-amber-600" />
          <h3 className="text-lg font-bold text-gray-800">Workshop Recordings</h3>
        </div>
        <Button variant="outline" size="sm" onClick={fetchList} data-testid="wr-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
        <p className="text-sm font-medium text-gray-700 mb-1">Upload a new recording (only paid workshop buyers can watch it)</p>
        <p className="text-xs text-gray-500 mb-3">Any size supported. If the upload drops, just re-select the same file and click Upload — it resumes from where it stopped.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <Input placeholder="Title (e.g. Day 1 — Foundation)" value={title} onChange={(e) => setTitle(e.target.value)} className="border-gray-300" data-testid="wr-title-input" />
          <input ref={fileRef} type="file" accept="video/*" onChange={onFileChange} className="text-sm text-gray-600" data-testid="wr-file-input" />
          <Button onClick={handleUpload} disabled={uploading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="wr-upload-btn">
            {resumeHint && !uploading ? <RotateCcw className="w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
            {uploading ? `${progress}%` : (resumeHint ? 'Resume Upload' : 'Upload')}
          </Button>
        </div>
        {resumeHint && !uploading && (
          <p className="mt-2 text-xs text-amber-700 flex items-center gap-1" data-testid="wr-resume-hint"><RotateCcw className="w-3.5 h-3.5" /> {resumeHint}</p>
        )}
        {uploading && (
          <>
            <div className="mt-3 h-2 bg-amber-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
            {statusText && <p className="mt-2 text-xs text-gray-600" data-testid="wr-status-text">{statusText}</p>}
          </>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-semibold">#</th>
              <th className="px-4 py-2 font-semibold">Title</th>
              <th className="px-4 py-2 font-semibold">Size</th>
              <th className="px-4 py-2 font-semibold">Uploaded</th>
              <th className="px-4 py-2 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No recordings uploaded yet.</td></tr>
            ) : (
              items.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid={`wr-row-${i}`}>
                  <td className="px-4 py-2 text-gray-500">{r.order || i + 1}</td>
                  <td className="px-4 py-2 font-medium text-gray-800">{r.title}</td>
                  <td className="px-4 py-2 text-gray-600">{r.size ? `${(r.size / (1024 * 1024)).toFixed(1)} MB` : '-'}</td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-'}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(r.id)} className="text-xs" data-testid={`wr-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkshopRecordingsAdmin;
